import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, Check, AlertTriangle } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedPagesGrid, type RelatedPageCard } from "@/components/RelatedPagesGrid";
import { SCHEMA_LOCALBUSINESS_ID, SITE_URL } from "@/lib/entitySchema";

export type ListicleItem = {
  rank: number;
  name: string;
  url?: string;
  city?: string;
  whatWeLike: string[];
  watchOuts?: string[];
  takeaway?: string;
};

export type ListiclePageConfig = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  breadcrumbs: { label: string; to?: string }[];
  currentLabel: string;
  eyebrow: string;
  h1: ReactNode;
  heroDescription: string;
  intro: string;
  methodology: string[];
  items: ListicleItem[];
  closing: string;
  relatedLinks: { to: string; label: string; description?: string }[];
  datePublished?: string;
};

export function buildListicleHead(config: ListiclePageConfig) {
  const selfUrl = `${SITE_URL}/${config.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${selfUrl}#article`,
        headline: config.metaTitle,
        description: config.metaDescription,
        datePublished: config.datePublished ?? "2026-01-01",
        dateModified: new Date().toISOString().slice(0, 10),
        author: { "@id": SCHEMA_LOCALBUSINESS_ID },
        publisher: { "@id": SCHEMA_LOCALBUSINESS_ID },
        mainEntityOfPage: selfUrl,
      },
      {
        "@type": "ItemList",
        "@id": `${selfUrl}#itemlist`,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: config.items.length,
        itemListElement: config.items.map((item) => ({
          "@type": "ListItem",
          position: item.rank,
          name: item.name,
          url: item.url,
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${selfUrl}#breadcrumbs`,
        itemListElement: [
          ...config.breadcrumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.label,
            item: c.to ? `${SITE_URL}${c.to}` : undefined,
          })),
          {
            "@type": "ListItem",
            position: config.breadcrumbs.length + 1,
            name: config.currentLabel,
            item: selfUrl,
          },
        ],
      },
    ],
  };

  return {
    meta: [
      { title: config.metaTitle },
      { name: "description", content: config.metaDescription },
      { property: "og:title", content: config.metaTitle },
      { property: "og:description", content: config.metaDescription },
      { property: "og:url", content: selfUrl },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: selfUrl }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  };
}

export function ListiclePage({ config }: { config: ListiclePageConfig }) {
  return (
    <SiteLayout>
      <PageHero
        eyebrow={config.eyebrow}
        title={config.h1}
        description={config.heroDescription}
      >
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md border-2 border-brand-gold/40 bg-card/10 px-6 py-3 text-base font-bold text-brand-gold backdrop-blur hover:border-brand-gold hover:bg-card/20"
            aria-label="Call (719) 419-3935"
          >
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>
      </PageHero>

      <nav aria-label="Breadcrumb" className="border-b border-border bg-muted/30">
        <div className="mx-auto max-w-5xl px-5 py-4 lg:px-8">
          <Breadcrumbs items={[...config.breadcrumbs, { label: config.currentLabel }]} />
        </div>
      </nav>

      <section className="bg-background py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-foreground/85">{config.intro}</p>

          <div className="mt-10 rounded-2xl border border-border bg-muted/30 p-6">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-brand-gold-deep">
              How we ranked them
            </h2>
            <ul className="mt-3 space-y-2">
              {config.methodology.map((m) => (
                <li key={m} className="flex gap-3 text-sm leading-relaxed text-foreground/85">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold-deep" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-section-alt py-16">
        <div className="mx-auto max-w-3xl space-y-8 px-5 lg:px-8">
          {config.items.map((item) => (
            <article
              key={item.rank}
              className="rounded-2xl border border-border bg-card p-6 shadow-card lg:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-gold text-xl font-black text-brand-black">
                  {item.rank}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-extrabold text-foreground">{item.name}</h3>
                  {item.city && (
                    <div className="mt-1 text-sm text-muted-foreground">{item.city}</div>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  What we like
                </div>
                <ul className="mt-2 space-y-1.5">
                  {item.whatWeLike.map((w) => (
                    <li key={w} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold-deep" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {item.watchOuts && item.watchOuts.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Watch out for
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {item.watchOuts.map((w) => (
                      <li key={w} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {item.takeaway && (
                <p className="mt-4 border-t border-border pt-4 text-sm italic leading-relaxed text-foreground/75">
                  {item.takeaway}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="bg-background py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-foreground/85">{config.closing}</p>
        </div>
      </section>

      <RelatedPagesGrid
        cards={config.relatedLinks.map<RelatedPageCard>((l) => ({
          to: l.to,
          title: l.label,
          description: l.description ?? `Read more about ${l.label.toLowerCase()}.`,
        }))}
      />

      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want a website that ranks like the top picks?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Owner-led, hand-coded, mobile-first. Built in Colorado, for Colorado.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold hover:-translate-y-0.5"
            >
              Get a Free Proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground hover:border-brand-gold/60"
              aria-label="Call (719) 419-3935"
            >
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
